import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, map } from 'rxjs';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { FieldIntervention, FieldInterventionsService } from '@connect-services/src/sdk/api';

export const fieldInterventionsBaseRouteRegex = `\\/${RoutePathEnum.FIELD_INTERVENTIONS}\\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}`;

export const changeSelectionFieldInterventionRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION}`,
    'i',
);

export const createFieldInterventionRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_CREATE}`,
    'i',
);

export const planningFieldInterventionRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_PLANNING}`,
    'i',
);

export const detailPanelOutletRegex = new RegExp(/\(detailPanel:([a-zA-Z0-9-/-]+)\)/);
export const lastUrlPathValueRegexp = new RegExp(/[^\\/?#]+(?=(\?|#|$))/);

export const dashboardFieldInterventionRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_DASHBOARD}`,
    'i',
);

export const fieldInterventionEntitiesRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_ENTITIES}`,
    'i',
);

export const fieldInterventionLayersRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_LAYERS}`,
    'i',
);

export const fieldInterventionFormsRegexp = new RegExp(
    `${fieldInterventionsBaseRouteRegex}\\/${RoutePathEnum.FIELD_INTERVENTION_FORMS}`,
    'i',
);

export const managementDataRegexp = new RegExp(`\\/${RoutePathEnum.MANAGEMENT_DATA}`, 'i');
export const managementOrganizationRegexp = new RegExp(`\\/${RoutePathEnum.MANAGEMENT_ORGANIZATION}`, 'i');

@Injectable({
    providedIn: 'root',
})
export class CanNavigateGuard implements CanActivateChild {
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly fieldInterventionsService = inject(FieldInterventionsService);
    private readonly translocoService = inject(TranslocoService);

    public constructor(
        private router: Router,
        private matDialog: MatDialog,
    ) {}

    public async canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const currentUrl = this.router.url;
        const targetUrl = state.url;

        const isSelectingEntities =
            createFieldInterventionRegexp.test(currentUrl) || changeSelectionFieldInterventionRegexp.test(currentUrl);

        if (
            isSelectingEntities &&
            !planningFieldInterventionRegexp.test(targetUrl) &&
            !this.createFieldInterventionService.isSelectionEntitiesValidated()
        ) {
            const dialogRef = this.matDialog.open(ConfirmationModal, {
                autoFocus: false,
                data: {
                    cancelLabel: this.translocoService.translate('cancel'),
                    confirmLabel: this.translocoService.translate('quit'),
                    description: this.translocoService.translate('entityNotSaved'),
                    title: this.translocoService.translate('leavePage'),
                    type: ModalType.DANGER,
                },
            });

            const result = await firstValueFrom(
                dialogRef.afterClosed().pipe(map((result: ModalResultEnum) => result === ModalResultEnum.CONFIRM)),
            );

            if (result) {
                if (
                    createFieldInterventionRegexp.test(currentUrl) ||
                    changeSelectionFieldInterventionRegexp.test(currentUrl)
                ) {
                    this.createFieldInterventionService.isSelectionEntitiesValidated.set(true);
                }

                if (createFieldInterventionRegexp.test(currentUrl)) {
                    const fieldInterventionId = currentUrl.split('/').at(-2);
                    AssertService.isString(fieldInterventionId);

                    const fieldIntervention = await firstValueFrom(
                        this.fieldInterventionsService.fieldInterventionControllerGetOne({ id: fieldInterventionId }),
                    );

                    if (fieldIntervention?.status === FieldIntervention.StatusEnum.TO_PLAN) {
                        await firstValueFrom(
                            this.fieldInterventionsService.fieldInterventionControllerDelete({
                                id: fieldInterventionId,
                            }),
                        );
                    }
                }
            }

            if (!result) {
                throw new Error(`Can't navigate to this page`);
            }

            return result;
        }

        return true;
    }
}
